/**
 * RegionalFoodsData.ts
 * 15 iconic regional specialty foods across the 5 flagship US states (3 per state).
 * Includes cost ($ USD), Hunger restoration, Happiness restoration, and temporary active buffs.
 * Aligns strictly with domain-oracle.js and survey_report.md.
 */

import { StateId } from './FlagshipStatesRegistry';
import { BuffType } from '../core/Types';

export interface FoodBuffDefinition {
  id: string;
  name: string;
  type: BuffType;
  magnitude: number;
  durationSec: number;
  description: string;
}

export interface RegionalFoodItem {
  id: string;
  stateId: StateId;
  name: string;
  vietnameseName: string;
  description: string;
  costUSD: number;
  hungerRestore: number;
  happinessRestore: number;
  buff: FoodBuffDefinition;
  icon: string;
}

export const REGIONAL_FOODS: RegionalFoodItem[] = [
  // --- New York (NY) ---
  {
    id: 'food_ny_slice',
    stateId: 'NY',
    name: 'NY Slice Pizza',
    vietnameseName: 'Pizza Gập Kiểu New York',
    description: 'Miếng pizza phô mai kéo sợi thơm lừng nướng lò gạch, biểu tượng ẩm thực hè phố New York.',
    costUSD: 32,
    hungerRestore: 45,
    happinessRestore: 20,
    buff: {
      id: 'buff_ny_slice',
      name: 'Metropolitan Hustle',
      type: 'PAY_BOOST',
      magnitude: 0.20,
      durationSec: 240,
      description: '+20% Thù lao ca làm việc trong 4 phút'
    },
    icon: '🍕'
  },
  {
    id: 'food_ny_bagel',
    stateId: 'NY',
    name: 'Everything Bagel & Schmear',
    vietnameseName: 'Bánh Mì Vòng Phết Kem Phô Mai',
    description: 'Bánh mì bagel rắc đủ loại hạt kèm lớp kem phô mai béo ngậy khởi đầu ngày mới đầy năng lượng.',
    costUSD: 14,
    hungerRestore: 30,
    happinessRestore: 12,
    buff: {
      id: 'buff_ny_bagel',
      name: 'Morning Grit',
      type: 'ENERGY_SAVE',
      magnitude: 0.25,
      durationSec: 180,
      description: '-25% Tiêu hao Năng lượng khi hoạt động trong 3 phút'
    },
    icon: '🥯'
  },
  {
    id: 'food_ny_cheesecake',
    stateId: 'NY',
    name: 'Classic NY Cheesecake',
    vietnameseName: 'Bánh Phô Mai Nướng Cổ Điển New York',
    description: 'Món tráng miệng huyền thoại sánh mịn béo ngậy làm bừng sáng mọi tâm trạng u ám.',
    costUSD: 75,
    hungerRestore: 35,
    happinessRestore: 55,
    buff: {
      id: 'buff_ny_cheesecake',
      name: 'Sugar Euphoria',
      type: 'HAPPINESS_SHIELD',
      magnitude: 0.55,
      durationSec: 420,
      description: 'Giảm 55% tốc độ tụt Tinh thần trong 7 phút'
    },
    icon: '🍰'
  },

  // --- California (CA) ---
  {
    id: 'food_ca_double_burger',
    stateId: 'CA',
    name: 'In-N-Out Double Burger',
    vietnameseName: 'Burger Bò Đôi Kiểu Bờ Tây',
    description: 'Hai lớp bò tươi nướng xèo xèo kèm sốt đặc biệt trứ danh của chuỗi burger huyền thoại California.',
    costUSD: 85,
    hungerRestore: 65,
    happinessRestore: 35,
    buff: {
      id: 'buff_ca_burger',
      name: 'West Coast Powerhouse',
      type: 'PAY_BOOST',
      magnitude: 0.25,
      durationSec: 360,
      description: '+25% Thù lao ca làm việc trong 6 phút'
    },
    icon: '🍔'
  },
  {
    id: 'food_ca_avocado_toast',
    stateId: 'CA',
    name: 'Sourdough Avocado Toast',
    vietnameseName: 'Bánh Mì Men Chua Bơ Quả Bờ Tây',
    description: 'Bơ sáp nghiền bùi bùi trên lát bánh mì men chua nướng giòn, món ăn khoái khẩu của dân tech Silicon Valley.',
    costUSD: 38,
    hungerRestore: 40,
    happinessRestore: 22,
    buff: {
      id: 'buff_ca_avocado',
      name: 'Silicon Valley Flow',
      type: 'STUDY_BOOST',
      magnitude: 0.30,
      durationSec: 300,
      description: '+30% Tốc độ học chứng chỉ chuyên môn trong 5 phút'
    },
    icon: '🥑'
  },
  {
    id: 'food_ca_napa_smoothie',
    stateId: 'CA',
    name: 'Napa Valley Berry Smoothie',
    vietnameseName: 'Sinh Tố Quả Mọng Thung Lũng Napa',
    description: 'Ly sinh tố dâu rừng hữu cơ thanh mát giải nhiệt tức thì, bồi bổ cơ thể tươi tắn.',
    costUSD: 16,
    hungerRestore: 22,
    happinessRestore: 25,
    buff: {
      id: 'buff_ca_smoothie',
      name: 'Organic Vitality',
      type: 'ENERGY_SAVE',
      magnitude: 0.22,
      durationSec: 240,
      description: '-22% Tiêu hao Năng lượng trong 4 phút'
    },
    icon: '🥤'
  },

  // --- Texas (TX) ---
  {
    id: 'food_tx_brisket',
    stateId: 'TX',
    name: 'Smoked Brisket BBQ',
    vietnameseName: 'Nạm Bò Hun Khói Gỗ Sồi Texas',
    description: 'Từng thớ nạm bò mềm tan trong miệng sau 14 giờ hun khói gỗ sồi, mang sức mạnh hoang dã miền Tây.',
    costUSD: 110,
    hungerRestore: 75,
    happinessRestore: 45,
    buff: {
      id: 'buff_tx_brisket',
      name: 'Lone Star Power',
      type: 'PAY_BOOST',
      magnitude: 0.35,
      durationSec: 420,
      description: '+35% Thù lao ca làm việc trong 7 phút'
    },
    icon: '🥩'
  },
  {
    id: 'food_tx_tacos',
    stateId: 'TX',
    name: 'Tex-Mex Street Tacos',
    vietnameseName: 'Bánh Tacos Đường Phố Tex-Mex',
    description: 'Vỏ bánh ngô giòn tan kẹp thịt nướng cay nồng và ớt jalapeno đánh thức mọi giác quan.',
    costUSD: 18,
    hungerRestore: 35,
    happinessRestore: 15,
    buff: {
      id: 'buff_tx_tacos',
      name: 'Spicy Kick',
      type: 'ENERGY_SAVE',
      magnitude: 0.20,
      durationSec: 210,
      description: '-20% Tiêu hao Năng lượng trong 3.5 phút'
    },
    icon: '🌮'
  },
  {
    id: 'food_tx_pecan_pie',
    stateId: 'TX',
    name: 'Southern Pecan Pie',
    vietnameseName: 'Bánh Hồ Đào Miền Nam',
    description: 'Vỏ bánh bơ thơm bùi nhân hạt hồ đào ngọt dịu đặc trưng cho lòng hiếu khách của người dân Texas.',
    costUSD: 34,
    hungerRestore: 30,
    happinessRestore: 40,
    buff: {
      id: 'buff_tx_pie',
      name: 'Southern Hospitality',
      type: 'HAPPINESS_SHIELD',
      magnitude: 0.40,
      durationSec: 300,
      description: 'Giảm 40% tốc độ tụt Tinh thần trong 5 phút'
    },
    icon: '🥧'
  },

  // --- Florida (FL) ---
  {
    id: 'food_fl_cuban_sandwich',
    stateId: 'FL',
    name: 'Authentic Cuban Sandwich',
    vietnameseName: 'Bánh Mì Kẹp Thịt Kiểu Cuba',
    description: 'Thịt heo quay, giăm bông, dưa chua và phô mai nướng ép giòn rụm theo phong cách Little Havana Miami.',
    costUSD: 44,
    hungerRestore: 50,
    happinessRestore: 25,
    buff: {
      id: 'buff_fl_cuban',
      name: 'Calle Ocho Heat',
      type: 'PAY_BOOST',
      magnitude: 0.22,
      durationSec: 300,
      description: '+22% Thù lao ca làm việc trong 5 phút'
    },
    icon: '🥪'
  },
  {
    id: 'food_fl_key_lime_pie',
    stateId: 'FL',
    name: 'Key West Key Lime Pie',
    vietnameseName: 'Bánh Chanh Key West',
    description: 'Món bánh chanh chua ngọt thanh tao với lớp kem bông tuyết xua tan cái nóng bức nhiệt đới.',
    costUSD: 78,
    hungerRestore: 35,
    happinessRestore: 50,
    buff: {
      id: 'buff_fl_lime',
      name: 'Tropical Breeze',
      type: 'HAPPINESS_SHIELD',
      magnitude: 0.50,
      durationSec: 420,
      description: 'Giảm 50% tốc độ tụt Tinh thần trong 7 phút'
    },
    icon: '🍋'
  },
  {
    id: 'food_fl_orange_juice',
    stateId: 'FL',
    name: 'Freshly Squeezed Sunshine OJ',
    vietnameseName: 'Nước Cam Tươi Vắt Florida',
    description: 'Ly nước cam tươi mọng nước dồi dào Vitamin C mang ánh nắng rực rỡ của bang Ánh Nắng Mặt Trời.',
    costUSD: 12,
    hungerRestore: 20,
    happinessRestore: 20,
    buff: {
      id: 'buff_fl_oj',
      name: 'Vitamin Sunshine',
      type: 'ENERGY_SAVE',
      magnitude: 0.20,
      durationSec: 180,
      description: '-20% Tiêu hao Năng lượng trong 3 phút'
    },
    icon: '🍊'
  },

  // --- Washington (WA) ---
  {
    id: 'food_wa_espresso',
    stateId: 'WA',
    name: 'Seattle Double Espresso',
    vietnameseName: 'Cà Phê Espresso Đậm Đặc Seattle',
    description: 'Một shot cà phê đậm đặc hạt Arabica rang mộc từ thủ phủ cà phê Seattle kích thích não bộ tập trung cao độ.',
    costUSD: 15,
    hungerRestore: 15,
    happinessRestore: 25,
    buff: {
      id: 'buff_wa_espresso',
      name: 'Pacific Roast Focus',
      type: 'STUDY_BOOST',
      magnitude: 0.35,
      durationSec: 300,
      description: '+35% Tốc độ học chứng chỉ chuyên môn trong 5 phút'
    },
    icon: '☕'
  },
  {
    id: 'food_wa_cedar_salmon',
    stateId: 'WA',
    name: 'Cedar Plank King Salmon',
    vietnameseName: 'Cá Hồi Vua Nướng Gỗ Tuyết Tùng',
    description: 'Cá hồi tự nhiên vùng Tây Bắc nướng trên phiến gỗ tuyết tùng đượm khói, bổ sung omega-3 dồi dào.',
    costUSD: 120,
    hungerRestore: 70,
    happinessRestore: 50,
    buff: {
      id: 'buff_wa_salmon',
      name: 'Northwest Synergy',
      type: 'PAY_BOOST',
      magnitude: 0.32,
      durationSec: 480,
      description: '+32% Thù lao ca làm việc trong 8 phút'
    },
    icon: '🐟'
  },
  {
    id: 'food_wa_cherry_tart',
    stateId: 'WA',
    name: 'Rainier Cherry Tart',
    vietnameseName: 'Bánh Tart Cherry Rainier Núi Rainier',
    description: 'Quả cherry vàng mọng nước ngọt ngào từ thung lũng núi lửa Rainier tạo lớp khiên bảo vệ tinh thần hoàn hảo.',
    costUSD: 42,
    hungerRestore: 32,
    happinessRestore: 35,
    buff: {
      id: 'buff_wa_cherry',
      name: 'Cascade Bliss',
      type: 'HAPPINESS_SHIELD',
      magnitude: 0.45,
      durationSec: 300,
      description: 'Giảm 45% tụt Tinh thần và chặn biến cố tiêu cực trong 5 phút'
    },
    icon: '🍒'
  }
];

export function getFoodById(foodId: string): RegionalFoodItem | undefined {
  return REGIONAL_FOODS.find((f) => f.id === foodId);
}

export function getFoodsForState(stateId: StateId): RegionalFoodItem[] {
  return REGIONAL_FOODS.filter((f) => f.stateId === stateId);
}
